import React, { useState, useEffect, useRef, useCallback } from 'react';
import '@/App.css';
import '@/styles/spacing.css';
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { Moon, Sun, FileText, Scissors, Archive, FileType, Image as ImageIcon, Upload, Download, Crown, Activity, Users, TrendingUp, DollarSign, RefreshCw } from 'lucide-react';

// Legal Pages
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import CookiePolicy from '@/pages/CookiePolicy';
import RefundPolicy from '@/pages/RefundPolicy';
import CopyrightDMCA from '@/pages/CopyrightDMCA';
import Disclaimer from '@/pages/Disclaimer';
import AcceptableUsePolicy from '@/pages/AcceptableUsePolicy';
import ContactSupport from '@/pages/ContactSupport';
import Security from '@/pages/Security';
import AdminOffers from '@/pages/AdminOffers';
import AdminCodes from '@/pages/AdminCodes';
import AdminSettings from '@/pages/AdminSettings';

// Components
import AnnouncementBanner from '@/components/AnnouncementBanner';
import ClaimModal from '@/components/ClaimModal';
import LaunchOfferModal from '@/components/LaunchOfferModal';
import TrackingScripts from '@/components/TrackingScripts';
import SecurityNotice from '@/components/SecurityNotice';
import Snowfall from '@/components/Snowfall';
import HolidayPopup from '@/components/HolidayPopup';
import ToolInstructionsPanel from '@/components/ToolInstructionsPanel';
import NovaChatbot from '@/components/NovaChatbot';
import logger from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Programmatic file download that respects server's Content-Disposition: attachment
// and avoids popup-blocker issues associated with window.open('_blank').
// Fetches the file as a blob, creates an object URL, and triggers an <a download>
// click. Works reliably across Chrome, Safari, Firefox, Edge for any MIME type.
const triggerFileDownload = async (url, filename) => {
  try {
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1500);
  } catch (err) {
    logger.error('File download failed:', err);
    toast.error('Download failed. Please try again.');
  }
};

// Token refresh utilities
const TOKEN_REFRESH_THRESHOLD = 30 * 60 * 1000; // Refresh 30 minutes before expiry
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch {
    return 0;
  }
};

const shouldRefreshToken = (token) => {
  if (!token) return false;
  const expiry = getTokenExpiry(token);
  const now = Date.now();
  return expiry - now < TOKEN_REFRESH_THRESHOLD && expiry > now;
};

const refreshAuthToken = async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    const response = await axios.post(`${API}/user/refresh-token`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const newToken = response.data.access_token;
    localStorage.setItem('token', newToken);
    return newToken;
  } catch (error) {
    // If refresh fails, clear auth state
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    return null;
  }
};

// Axios interceptor for automatic token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 error and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      const errorDetail = error.response?.data?.detail || '';
      
      // Only attempt refresh for token-related errors
      if (errorDetail.includes('expired') || errorDetail.includes('Token')) {
        originalRequest._retry = true;
        
        if (!isRefreshing) {
          isRefreshing = true;
          
          try {
            const newToken = await refreshAuthToken();
            isRefreshing = false;
            
            if (newToken) {
              onTokenRefreshed(newToken);
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            } else {
              // Refresh failed - trigger logout
              window.dispatchEvent(new CustomEvent('auth-expired'));
              return Promise.reject(error);
            }
          } catch (refreshError) {
            isRefreshing = false;
            window.dispatchEvent(new CustomEvent('auth-expired'));
            return Promise.reject(refreshError);
          }
        } else {
          // Wait for token refresh
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken) => {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              resolve(axios(originalRequest));
            });
          });
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Proactive token refresh - check periodically
setInterval(() => {
  const token = localStorage.getItem('token');
  if (token && shouldRefreshToken(token)) {
    refreshAuthToken().then(newToken => {
      if (newToken) {
        logger.log('Token refreshed proactively');
      }
    });
  }
}, 5 * 60 * 1000); // Check every 5 minutes

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <Toaster position="top-right" />
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/copyright-dmca" element={<CopyrightDMCA />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />
          <Route path="/contact-support" element={<ContactSupport />} />
          <Route path="/security" element={<Security />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/offers" element={<AdminOffers />} />
          <Route path="/admin/codes" element={<AdminCodes />} />
        </Routes>
        <NovaChatbot />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

const Home = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: enter email, 2: enter code
  const [userStats, setUserStats] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [compressionQuality, setCompressionQuality] = useState(75);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showLaunchOfferModal, setShowLaunchOfferModal] = useState(false);
  const [bannerSettings, setBannerSettings] = useState(null);
  const [seoSettings, setSeoSettings] = useState(null);
  const [analyticsSettings, setAnalyticsSettings] = useState(null);
  const [contentSettings, setContentSettings] = useState(null);
  const [launchOfferSettings, setLaunchOfferSettings] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionsTool, setInstructionsTool] = useState(null);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [setupEmail, setSetupEmail] = useState('');
  const [seasonalSettings, setSeasonalSettings] = useState({ snow_enabled: true, holiday_popup_enabled: true });
  const [hasSeenHolidayPopup, setHasSeenHolidayPopup] = useState(false);
  const [toolVisibility, setToolVisibility] = useState({
    merge: true, split: true, compress: true, convert_to_jpg: true,
    pdf_to_word: true, word_to_pdf: true, xfa_to_pdf: true, image_to_pdf: true,
    pdf_to_ppt: true
  });
  const navigate = useNavigate();
  
  // Ref for scrolling to upload section
  const uploadSectionRef = useRef(null);
  
  // Function to scroll to upload section with centering
  const scrollToUploadSection = useCallback(() => {
    setTimeout(() => {
      if (uploadSectionRef.current) {
        const element = uploadSectionRef.current;
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const viewportHeight = window.innerHeight;
        const elementHeight = elementRect.height;
        
        // Calculate position to center the element in viewport
        const scrollToPosition = absoluteElementTop - (viewportHeight / 2) + (elementHeight / 2);
        
        window.scrollTo({
          top: Math.max(0, scrollToPosition - 50), // 50px offset for better visual
          behavior: 'smooth'
        });
      }
    }, 100); // Small delay to ensure DOM is updated
  }, []);

  // Pre-fill email from offer claim if available
  useEffect(() => {
    const offerEmail = localStorage.getItem('offerEmail');
    if (offerEmail && !isAuthenticated) {
      setEmail(offerEmail);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      fetchUserStats();
    }
    
    // Check saved theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }

    // Fetch banner settings
    fetchBannerSettings();

    // Fetch SEO settings
    fetchSEOSettings();

    // Fetch Analytics settings
    fetchAnalyticsSettings();

    // Fetch Content settings
    fetchContentSettings();

    // Fetch Launch Offer settings
    fetchLaunchOfferSettings();

    // Fetch Seasonal settings
    fetchSeasonalSettings();

    // Fetch Tool Visibility settings
    fetchToolVisibility();
    
    // Listen for auth expiry events (from token refresh failure)
    const handleAuthExpired = () => {
      setIsAuthenticated(false);
      setUserStats(null);
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      toast.error('Session expired. Please login again.');
      setShowAuth(true);
      setAuthMode('login');
    };
    
    window.addEventListener('auth-expired', handleAuthExpired);
    
    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  // Fetch tool visibility settings
  const fetchToolVisibility = async () => {
    try {
      const response = await axios.get(`${API}/settings/tools`);
      setToolVisibility(response.data);
    } catch (error) {
      console.error('Error fetching tool visibility:', error);
    }
  };

  // Fetch seasonal settings
  const fetchSeasonalSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/seasonal`);
      setSeasonalSettings(response.data);
    } catch (error) {
      console.error('Error fetching seasonal settings:', error);
    }
  };

  // Check if user has seen holiday popup when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem('token');
      if (token) {
        // Check user's holiday popup status from their stats
        axios.get(`${API}/user/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(response => {
          setHasSeenHolidayPopup(response.data.has_seen_holiday_popup || false);
        }).catch(() => {
          setHasSeenHolidayPopup(false);
        });
      }
    }
  }, [isAuthenticated]);

  // Handle holiday popup close
  const handleHolidayPopupClose = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/user/holiday-popup-seen`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setHasSeenHolidayPopup(true);
    } catch (error) {
      console.error('Error marking holiday popup seen:', error);
      setHasSeenHolidayPopup(true); // Still hide the popup even if API fails
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
    localStorage.setItem('theme', !darkMode ? 'dark' : 'light');
  };

  const fetchUserStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchBannerSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/banner`);
      setBannerSettings(response.data);
    } catch (error) {
      console.error('Error fetching banner settings:', error);
      // Set default settings if fetch fails
      setBannerSettings({
        banner_mode: 'launch',
        launch_banner_text: null,
        promo_banner_text: null,
        banner_background_color: null,
        banner_text_color: null,
        enable_close_button: true
      });
    }
  };

  const fetchSEOSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/seo`);
      setSeoSettings(response.data);
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
      // Set default settings if fetch fails
      setSeoSettings({
        meta_title: 'SmartPDF Tools - Professional PDF Processing Online',
        meta_description: 'Merge, split, compress, and convert your PDFs with ease. Fast, secure, and production-ready PDF tools.',
        meta_keywords: 'PDF tools, merge PDF, split PDF, compress PDF, convert PDF, online PDF',
        og_title: 'SmartPDF Tools - Professional PDF Processing',
        og_description: 'Professional PDF tools for merging, splitting, compressing, and converting PDFs online.',
        og_image_url: '',
        canonical_url: '',
        robots_meta: 'index, follow',
        enable_sitemap: true,
        enable_robots: true
      });
    }
  };

  const fetchAnalyticsSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/analytics`);
      setAnalyticsSettings(response.data);
    } catch (error) {
      console.error('Error fetching analytics settings:', error);
      // Set empty settings if fetch fails
      setAnalyticsSettings({
        google_analytics_id: null,
        google_tag_manager_id: null,
        facebook_pixel_id: null,
        linkedin_insight_id: null,
        microsoft_ads_id: null,
        tiktok_pixel_id: null
      });
    }
  };

  const fetchContentSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/content`);
      setContentSettings(response.data);
    } catch (error) {
      console.error('Error fetching content settings:', error);
      // Set defaults if fetch fails
      setContentSettings({
        homepage_title: "SmartPDF Tools",
        homepage_subtitle: "A fast, clean, ad-free alternative to iLovePDF & SmallPDF for only $5/month.",
        homepage_cta_text: "Get Started",
        header_promo_text: "A fast, clean, ad-free alternative to iLovePDF & SmallPDF.",
        header_promo_enabled: true,
        footer_promo_text: "A fast, clean, ad-free PDF tool for everyday use.",
        footer_copyright: "© 2025 SmartPDF Tools. All rights reserved.",
        pricing_title: "Simple, Affordable Pricing",
        pricing_subtitle: "Only $5/month for unlimited ad-free PDF tools.",
        meta_title: "SmartPDF Tools – Fast, Clean, Ad-Free PDF Tools",
        meta_description: "A faster, cleaner alternative to iLovePDF & SmallPDF. Merge, compress, convert PDFs without ads or limits for only $5/month."
      });
    }
  };


  const fetchLaunchOfferSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/launch-offer`);
      setLaunchOfferSettings(response.data);
    } catch (error) {
      console.error('Error fetching launch offer settings:', error);
      // Set default settings if fetch fails
      setLaunchOfferSettings({
        launch_offer_enabled: true,
        launch_offer_text: '🎉 Enjoy 1 month of Premium FREE with all new sign-ups. Offer ends Jan 15, 2026.',
        launch_offer_expiry: '2026-01-15T23:59:59Z',
        launch_offer_type: 'one_month_free'
      });
    }
  };

  const handleLaunchOfferExpiry = async () => {
    // When countdown reaches zero, disable the offer in the database
    try {
      // Update local state immediately
      setLaunchOfferSettings(prev => ({
        ...prev,
        launch_offer_enabled: false
      }));
      
      // No need to call backend - the registration logic already checks expiry
      logger.log('Launch offer expired and disabled');
    } catch (error) {
      console.error('Error handling launch offer expiry:', error);
    }
  };



  const handleClaimSuccess = (data) => {
    if (data.eligible) {
      // Account auto-created, prompt for password setup
      toast.success('Account created! Please set up your password to login.');
      setSetupEmail(email);  // Use the email from claim
      setShowClaimModal(false);
      setShowPasswordSetup(true);
      setEmail('');
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      // If login mode, first check if user needs password setup
      if (authMode === 'login') {
        const statusResponse = await axios.post(`${API}/user/check-password-status`, { email });
        
        if (statusResponse.data.needs_password_setup) {
          // User claimed offer but needs to set password
          setSetupEmail(email);
          setShowAuth(false);
          setShowPasswordSetup(true);
          setEmail('');
          setPassword('');
          toast.info('Please set up your password to complete your account setup');
          return;
        }
      }
      
      const endpoint = authMode === 'login' ? 'login' : 'register';
      const response = await axios.post(`${API}/user/${endpoint}`, { email, password });
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('email', response.data.email);
      setIsAuthenticated(true);
      setShowAuth(false);
      setEmail('');
      setPassword('');
      toast.success(`Successfully ${authMode === 'login' ? 'logged in' : 'registered'}!`);
      
      // Fetch user stats immediately after login to show subscription status
      fetchUserStats();
    } catch (error) {
      console.error(`${authMode} error:`, error);
      toast.error(error.response?.data?.detail || `${authMode} failed`);
    }
  };

  const handlePasswordSetup = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/user/setup-password`, {
        email: setupEmail,
        password: password
      });
      
      // Auto-login after password setup
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('email', response.data.email);
      setIsAuthenticated(true);
      setShowPasswordSetup(false);
      setSetupEmail('');
      setPassword('');
      toast.success('Password set up successfully! Welcome to SmartPDF Tools!');
      
      // Fetch user stats immediately after password setup to show subscription status
      fetchUserStats();
    } catch (error) {
      console.error('Password setup error:', error);
      toast.error(error.response?.data?.detail || 'Failed to set up password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    setIsAuthenticated(false);
    setUserStats(null);
    setEmail('');
    setPassword('');
    setShowAuth(false);
    setAuthMode('login');
    setSelectedTool(null);
    setFiles([]);
    setResults(null);
    toast.success('Logged out successfully');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/user/forgot-password`, { email });
      toast.success('Reset code sent to your email!');
      setResetStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/user/reset-password`, {
        email,
        reset_code: resetCode,
        new_password: newPassword
      });
      toast.success('Password updated successfully!');
      setShowForgotPassword(false);
      setResetStep(1);
      setResetCode('');
      setNewPassword('');
      setShowAuth(true);
      setAuthMode('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleFileSelect = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleProcessPDF = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to use PDF tools');
      setShowAuth(true);
      return;
    }

    if (files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    if (selectedTool === 'merge' && files.length < 2) {
      toast.error('Merge requires at least 2 files');
      return;
    }

    setProcessing(true);
    setResults(null);

    try {
      const formData = new FormData();
      
      if (selectedTool === 'merge' || selectedTool === 'image-to-pdf') {
        // These tools accept multiple files with the field name 'files'
        files.forEach(file => formData.append('files', file));
      } else {
        formData.append('file', files[0]);
      }
      
      // Add quality parameter for compress tool
      let apiUrl = `${API}/pdf/${selectedTool}`;
      
      // Map frontend tool IDs to backend endpoints
      if (selectedTool === 'pdf-to-word') {
        apiUrl = `${API}/pdf/convert-to-word`;
      }
      if (selectedTool === 'word-to-pdf') {
        apiUrl = `${API}/pdf/convert-from-word`;
      }
      if (selectedTool === 'xfa-to-pdf') {
        apiUrl = `${API}/pdf/flatten-xfa`;
      }
      if (selectedTool === 'image-to-pdf') {
        apiUrl = `${API}/pdf/convert-from-images`;
      }
      if (selectedTool === 'pdf-to-ppt') {
        apiUrl = `${API}/pdf/convert-to-ppt`;
      }
      
      if (selectedTool === 'compress') {
        apiUrl += `?quality=${compressionQuality}`;
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setResults(response.data);
      toast.success('PDF processed successfully!');
      fetchUserStats();
    } catch (error) {
      // Handle validation errors (422) and other errors properly
      const errorData = error.response?.data;
      let errorMessage = 'Processing failed';
      
      if (errorData) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation error - extract message from first error
          errorMessage = errorData.detail[0]?.msg || 'Validation error';
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpgradeToPro = async () => {
    // Redirect to Stripe payment link from environment variable
    const stripeUrl = process.env.REACT_APP_STRIPE_PRO_URL;
    if (!stripeUrl) {
      toast.error('Stripe configuration missing. Please contact support.');
      return;
    }
    window.location.href = stripeUrl;
  };

  const handleUpgradeToLifetime = async () => {
    // Redirect to Stripe payment link from environment variable
    const stripeUrl = process.env.REACT_APP_STRIPE_LIFETIME_URL;
    if (!stripeUrl) {
      toast.error('Stripe configuration missing. Please contact support.');
      return;
    }
    window.location.href = stripeUrl;
  };

  const handleUpgradeToYearly = async () => {
    // Redirect to Stripe payment link from environment variable
    const stripeUrl = process.env.REACT_APP_STRIPE_YEARLY_URL;
    if (!stripeUrl) {
      toast.error('Stripe configuration missing. Please contact support.');
      return;
    }
    window.location.href = stripeUrl;
  };

  // Tool visibility key mapping (frontend ID -> backend key)
  const toolVisibilityMap = {
    'merge': 'merge',
    'split': 'split',
    'compress': 'compress',
    'convert-to-jpg': 'convert_to_jpg',
    'pdf-to-word': 'pdf_to_word',
    'word-to-pdf': 'word_to_pdf',
    'xfa-to-pdf': 'xfa_to_pdf',
    'image-to-pdf': 'image_to_pdf',
    'pdf-to-ppt': 'pdf_to_ppt'
  };

  const allTools = [
    { id: 'merge', name: 'Merge PDFs', icon: <FileText className="w-8 h-8" />, description: 'Combine multiple PDFs into one file' },
    { id: 'split', name: 'Split PDF', icon: <Scissors className="w-8 h-8" />, description: 'Extract specific pages from PDF' },
    { id: 'compress', name: 'Compress PDF', icon: <Archive className="w-8 h-8" />, description: 'Reduce file size without quality loss' },
    { id: 'convert-to-jpg', name: 'PDF to JPG', icon: <ImageIcon className="w-8 h-8" />, description: 'Convert PDF pages to images' },
    { id: 'pdf-to-word', name: 'PDF to Word', icon: <FileText className="w-8 h-8" />, description: 'Convert PDF files into editable Word documents' },
    { id: 'word-to-pdf', name: 'Word to PDF', icon: <FileType className="w-8 h-8" />, description: 'Convert Word documents into clean PDF files' },
    { id: 'xfa-to-pdf', name: 'XFA to PDF', icon: <RefreshCw className="w-8 h-8" />, description: 'Convert unsupported XFA forms into standard PDF files' },
    { id: 'image-to-pdf', name: 'Image to PDF', icon: <ImageIcon className="w-8 h-8" />, description: 'Convert JPG, JPEG, or PNG images into a single PDF' },
    { id: 'pdf-to-ppt', name: 'PDF to PPT', icon: <FileType className="w-8 h-8" />, description: 'Convert slide-format PDFs back to editable PowerPoint files' },
  ];

  // Filter tools based on visibility settings
  const tools = allTools.filter(tool => {
    const visibilityKey = toolVisibilityMap[tool.id];
    return toolVisibility[visibilityKey] !== false;
  });

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-blue-50 via-slate-50 to-gray-100'}`}>
      {/* Dynamic SEO Meta Tags */}
      {seoSettings && (
        <Helmet>
          <title>{seoSettings.meta_title || 'SmartPDF Tools - Professional PDF Processing Online'}</title>
          <meta name="description" content={seoSettings.meta_description || 'Merge, split, compress, and convert your PDFs with ease.'} />
          {seoSettings.meta_keywords && <meta name="keywords" content={seoSettings.meta_keywords} />}
          <meta name="robots" content={seoSettings.robots_meta || 'index, follow'} />
          
          {/* Open Graph Tags */}
          <meta property="og:title" content={seoSettings.og_title || seoSettings.meta_title || 'SmartPDF Tools'} />
          <meta property="og:description" content={seoSettings.og_description || seoSettings.meta_description || 'Professional PDF tools online'} />
          {seoSettings.og_image_url && <meta property="og:image" content={seoSettings.og_image_url} />}
          <meta property="og:type" content="website" />
          
          {/* Canonical URL */}
          {seoSettings.canonical_url && <link rel="canonical" href={seoSettings.canonical_url} />}
        </Helmet>
      )}

      {/* Dynamic Analytics & Tracking Scripts */}
      <TrackingScripts analytics={analyticsSettings} />
      
      {/* Seasonal: Snowfall Animation */}
      <Snowfall enabled={seasonalSettings?.snow_enabled} />
      
      {/* Seasonal: Holiday Popup */}
      <HolidayPopup 
        isAuthenticated={isAuthenticated}
        hasSeenHolidayPopup={hasSeenHolidayPopup}
        onClose={handleHolidayPopupClose}
        enabled={seasonalSettings?.holiday_popup_enabled}
      />
      
      {/* Dynamic Banner Rendering based on settings */}
      {/* NEW Launch Offer Banner - Check if enabled and not expired */}
      {launchOfferSettings && 
       launchOfferSettings.launch_offer_enabled && 
       new Date(launchOfferSettings.launch_offer_expiry) > new Date() && (
        <AnnouncementBanner 
          onClaimClick={() => {
            // For the new launch offer, just show the sign-up modal
            setShowAuth(true);
            setAuthMode('register');
          }}
          customText={launchOfferSettings.launch_offer_text}
          backgroundColor="#FFF8D1"
          textColor="#1A1A1A"
          enableCloseButton={true}
          bannerType="launch"
          expiryDate={launchOfferSettings.launch_offer_expiry}
          onExpiry={handleLaunchOfferExpiry}
        />
      )}
      
      {/* Legacy Banner System - for promo codes */}
      {bannerSettings && bannerSettings.banner_mode !== 'none' && (
        <>          
          {/* Show Promo Banner if mode is 'promo' or 'both' */}
          {(bannerSettings.banner_mode === 'promo' || bannerSettings.banner_mode === 'both') && (
            <AnnouncementBanner 
              onClaimClick={() => setShowClaimModal(true)}
              customText={bannerSettings.promo_banner_text}
              backgroundColor={bannerSettings.banner_background_color}
              textColor={bannerSettings.banner_text_color}
              enableCloseButton={bannerSettings.enable_close_button}
              bannerType="promo"
            />
          )}
        </>
      )}
      
      {/* Launch Offer Modal - For free yearly offer */}
      <LaunchOfferModal 
        isOpen={showLaunchOfferModal}
        onClose={() => setShowLaunchOfferModal(false)}
        onSuccess={handleClaimSuccess}
      />
      
      {/* Promo Code Modal - For discount codes */}
      <ClaimModal 
        isOpen={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        onSuccess={handleClaimSuccess}
      />


      {/* Password Setup Modal */}
      {showPasswordSetup && (
        <Dialog open={showPasswordSetup} onOpenChange={setShowPasswordSetup}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="text-2xl">🔐</span>
                Set Up Your Password
              </DialogTitle>
              <DialogDescription>
                Your account has been created! Set up your password to complete your registration and access your free yearly subscription.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handlePasswordSetup}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="setup-email" className="block text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    id="setup-email"
                    type="email"
                    value={setupEmail}
                    disabled
                    className="w-full px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label htmlFor="setup-password" className="block text-sm font-medium mb-2">
                    Create Password
                  </label>
                  <input
                    id="setup-password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                  Complete Setup & Login
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/80">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>SmartPDF Tools</h1>
          </div>
          
          <div className="flex items-center space-x-4">
            {isAuthenticated && userStats && (
              <div className="hidden md:flex items-center space-x-4 mr-4">
                {/* Admin Dashboard Link */}
                {userStats.role === 'admin' && (
                  <div className="relative">
                    <Button
                      onClick={() => navigate('/admin/settings')}
                      size="sm"
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    >
                      ⚙️ Admin Dashboard
                    </Button>
                  </div>
                )}
                
                <div className="text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Today: </span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {userStats.operations_today}/{userStats.subscription_status === 'pro' || userStats.subscription_status === 'yearly' || userStats.subscription_status === 'lifetime' ? '∞' : '3'}
                  </span>
                </div>
                {userStats.subscription_status === 'free' && (
                  <Button 
                    data-testid="upgrade-pro-btn"
                    onClick={handleUpgradeToPro} 
                    size="sm" 
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  >
                    <Crown className="w-4 h-4 mr-1" />
                    Upgrade to Pro
                  </Button>
                )}
                {(userStats.subscription_status === 'pro' || userStats.subscription_status === 'yearly' || userStats.subscription_status === 'lifetime') && (
                  <span className="flex items-center text-sm font-semibold text-amber-600 dark:text-amber-400">
                    <Crown className="w-4 h-4 mr-1" /> {userStats.subscription_status === 'yearly' ? 'Yearly' : userStats.subscription_status === 'lifetime' ? 'Lifetime' : 'Pro'}
                  </span>
                )}
              </div>
            )}
            
            <Button
              data-testid="theme-toggle-btn"
              variant="outline"
              size="icon"
              onClick={toggleDarkMode}
              className="rounded-full"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            {!isAuthenticated ? (
              <Button data-testid="login-btn" onClick={() => setShowAuth(true)}>Login / Sign Up</Button>
            ) : (
              <Button data-testid="logout-btn" variant="outline" onClick={handleLogout}>Logout</Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{ paddingTop: 'var(--section-padding-y-lg)', paddingBottom: 'var(--section-padding-y)', paddingLeft: 'var(--container-padding-x)', paddingRight: 'var(--container-padding-x)' }}>
        <div className="container mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 'var(--space-lg)' }}>
            {contentSettings?.homepage_title || 'SmartPDF Tools'}
          </h2>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif', marginBottom: 'var(--space-2xl)' }}>
            {contentSettings?.homepage_subtitle || 'Merge, split, compress, and convert your PDFs with ease. Fast, secure, and production-ready.'}
          </p>
          
          {!isAuthenticated && (
            <div className="max-w-md mx-auto" style={{ marginBottom: 0 }}>
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-900 border-2 border-blue-200 dark:border-blue-800">
                <CardContent style={{ padding: 'var(--card-padding)' }}>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white" style={{ marginBottom: 'var(--space-sm)' }}>Get Started Free</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300" style={{ marginBottom: 'var(--space-md)' }}>
                    Create a free account to start using all PDF tools
                  </p>
                  <Button 
                    data-testid="hero-signup-btn"
                    onClick={() => setShowAuth(true)} 
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {contentSettings?.homepage_cta_text || 'Sign Up Free - No Credit Card Required'}
                  </Button>
                  <p className="text-xs text-slate-500 dark:text-slate-400" style={{ marginTop: 'var(--space-sm)' }}>
                    Free plan: 3 files/day • No credit card needed
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* AdSense Placeholder */}
          {isAuthenticated && userStats?.subscription_status === 'free' && (
            <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600" style={{ marginBottom: 0, padding: 'var(--space-2xl)' }}>
              <p className="text-sm text-slate-500 dark:text-slate-400">Google AdSense Placeholder</p>
              <p className="text-xs text-slate-400 dark:text-slate-500" style={{ marginTop: 'var(--space-xs)' }}>Ads removed for Pro users</p>
            </div>
          )}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-white dark:bg-slate-800" style={{ paddingTop: 'var(--section-padding-y-lg)', paddingBottom: 'var(--section-padding-y)', paddingLeft: 'var(--container-padding-x)', paddingRight: 'var(--container-padding-x)' }}>
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center text-slate-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 'var(--space-md)' }}>
            {contentSettings?.pricing_title || 'Choose Your Plan'}
          </h2>
          <p className="text-center text-slate-600 dark:text-slate-300 max-w-2xl mx-auto" style={{ marginBottom: 'var(--space-3xl)' }}>
            {contentSettings?.pricing_subtitle || 'Start free with 3 files per day, or upgrade for unlimited access'}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto" style={{ gap: 'var(--card-gap)' }}>
            {/* Free Plan */}
            <Card className="relative flex flex-col">
              <CardHeader>
                <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Free</CardTitle>
                <div className="text-3xl font-bold mt-4">$0</div>
                <CardDescription>For occasional use</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="space-y-2 flex-1">
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> 3 files/day
                  </p>
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> All PDF tools
                  </p>
                  <p className="flex items-center text-sm text-slate-400">
                    <span className="mr-2">—</span> Ads supported
                  </p>
                </div>
                <Button 
                  data-testid="free-plan-btn"
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => !isAuthenticated && setShowAuth(true)}
                >
                  {isAuthenticated ? 'Current Plan' : 'Get Started'}
                </Button>
              </CardContent>
            </Card>

            {/* Pro Monthly Plan */}
            <Card className="relative flex flex-col border-2 border-blue-500 shadow-lg">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Most Popular
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Pro</CardTitle>
                <div className="text-3xl font-bold mt-4">$5<span className="text-base text-slate-500">/month</span></div>
                <CardDescription>Unlimited monthly</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="space-y-2 flex-1">
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> <strong>Unlimited files</strong>
                  </p>
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> <strong>No ads</strong>
                  </p>
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> Priority support
                  </p>
                </div>
                <Button 
                  data-testid="pro-plan-btn"
                  className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    if (!isAuthenticated) {
                      setShowAuth(true);
                    } else {
                      handleUpgradeToPro();
                    }
                  }}
                >
                  {isAuthenticated && userStats?.subscription_status === 'pro' ? 'Current Plan' : 'Upgrade to Pro'}
                </Button>
              </CardContent>
            </Card>

            {/* Yearly Plan */}
            <Card className="relative flex flex-col border-2 border-purple-500 shadow-lg">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Save 17%
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Yearly</CardTitle>
                <div className="text-3xl font-bold mt-4">$50<span className="text-base text-slate-500">/year</span></div>
                <CardDescription>Best for power users</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="space-y-2 flex-1">
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> <strong>Unlimited files</strong>
                  </p>
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> <strong>No ads</strong>
                  </p>
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> Priority support
                  </p>
                  <p className="flex items-center text-sm">
                    <span className="text-purple-500 mr-2">✓</span> <strong>Save $10/year</strong>
                  </p>
                </div>
                <Button 
                  data-testid="yearly-plan-btn"
                  className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                  onClick={() => {
                    if (!isAuthenticated) {
                      setShowAuth(true);
                    } else {
                      handleUpgradeToYearly();
                    }
                  }}
                >
                  {isAuthenticated && userStats?.subscription_status === 'yearly' ? 'Current Plan' : 'Get Yearly'}
                </Button>
              </CardContent>
            </Card>

            {/* Lifetime Plan */}
            <Card className="relative flex flex-col border-2 border-amber-500 shadow-lg">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Best Value
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Lifetime</CardTitle>
                <div className="text-3xl font-bold mt-4">$39<span className="text-base text-slate-500"> once</span></div>
                <CardDescription>Pay once, use forever</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="space-y-2 flex-1">
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> <strong>Unlimited forever</strong>
                  </p>
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> <strong>No ads</strong>
                  </p>
                  <p className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">✓</span> Priority support
                  </p>
                  <p className="flex items-center text-sm">
                    <span className="text-amber-500 mr-2">✓</span> <strong>All future updates</strong>
                  </p>
                </div>
                <Button 
                  data-testid="lifetime-plan-btn"
                  className="w-full mt-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  onClick={() => {
                    if (!isAuthenticated) {
                      setShowAuth(true);
                    } else {
                      handleUpgradeToLifetime();
                    }
                  }}
                >
                  {isAuthenticated && userStats?.subscription_status === 'lifetime' ? 'Current Plan' : 'Get Lifetime'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Tools Grid - Only visible for authenticated users */}
      {isAuthenticated ? (
        <section style={{ paddingTop: 'var(--section-padding-y)', paddingBottom: 'var(--section-padding-y)', paddingLeft: 'var(--container-padding-x)', paddingRight: 'var(--container-padding-x)' }}>
          <div className="container mx-auto">
            <h3 className="text-3xl font-bold text-center text-slate-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 'var(--space-2xl)' }}>
              Your PDF Tools
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" style={{ gap: 'var(--card-gap)', marginBottom: 'var(--space-3xl)' }}>
              {tools.map((tool) => (
                <Card
                  key={tool.id}
                  data-testid={`tool-card-${tool.id}`}
                  className={`cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    selectedTool === tool.id ? 'ring-2 ring-blue-500 shadow-xl' : ''
                  }`}
                  onClick={() => {
                    setInstructionsTool(tool.id);
                    setShowInstructions(true);
                  }}
                >
                  <CardHeader>
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                      {tool.icon}
                    </div>
                    <CardTitle className="text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{tool.name}</CardTitle>
                    <CardDescription className="text-center">{tool.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>

            {/* File Upload Section */}
            {selectedTool && (
              <Card ref={uploadSectionRef} className="max-w-3xl mx-auto" id="upload-section">
                <CardHeader className="pb-4">
                  <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Upload Your Files</CardTitle>
                  <CardDescription>
                    {selectedTool === 'merge' ? 'Select 2 or more PDF files to merge' : 
                     selectedTool === 'word-to-pdf' ? 'Select a Word document (.docx) to convert' :
                     selectedTool === 'xfa-to-pdf' ? 'Select an XFA-based PDF form to convert' :
                     selectedTool === 'image-to-pdf' ? 'Select one or more images (JPG, PNG) to convert' :
                     selectedTool === 'pdf-to-ppt' ? 'Select a slide-format PDF (originally from PowerPoint, Keynote, or Google Slides) to convert back to PPTX' :
                     'Select a PDF file to process'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-2">
                  {/* Security Notice */}
                  <SecurityNotice />
                  
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                    <Input
                      data-testid="file-input"
                      type="file"
                      accept={selectedTool === 'word-to-pdf' ? '.docx,.doc' : selectedTool === 'image-to-pdf' ? '.jpg,.jpeg,.png,.heic' : '.pdf'}
                      multiple={selectedTool === 'merge' || selectedTool === 'image-to-pdf'}
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                      <p className="text-slate-600 dark:text-slate-300 mb-2">Click to upload or drag and drop</p>
                      <p className="text-sm text-slate-500">
                        {selectedTool === 'word-to-pdf' ? 'Word files only (.docx, .doc)' : 
                         selectedTool === 'image-to-pdf' ? 'Image files (JPG, JPEG, PNG, HEIC)' : 'PDF files only'}
                      </p>
                    </label>
                  </div>

                  {/* Compression Quality Selector - Only for Compress tool */}
                  {selectedTool === 'compress' && (
                    <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div>
                        <Label className="text-base font-semibold">Compression Quality</Label>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          Lower quality = smaller file size. Higher quality = better appearance.
                        </p>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {[
                          { value: 25, label: '25%', desc: 'Max compression' },
                          { value: 50, label: '50%', desc: 'High compression' },
                          { value: 75, label: '75%', desc: 'Balanced' },
                          { value: 85, label: '85%', desc: 'Light compression' },
                          { value: 90, label: '90%', desc: 'Minimal compression' }
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setCompressionQuality(option.value)}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              compressionQuality === option.value
                                ? 'border-blue-500 bg-blue-500 text-white shadow-lg'
                                : 'border-slate-300 dark:border-slate-600 hover:border-blue-400'
                            }`}
                          >
                            <div className="text-lg font-bold">{option.label}</div>
                            <div className="text-xs mt-1">{option.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {files.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Selected Files:</h4>
                      {files.map((file, idx) => (
                        <div key={`${file.name}-${file.size}-${idx}`} className="flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                          <span className="text-sm truncate">{file.name}</span>
                          <span className="text-xs text-slate-500">{(file.size / 1024).toFixed(2)} KB</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {processing && (
                    <div className="space-y-2">
                      <Progress value={66} className="w-full" />
                      <p className="text-sm text-center text-slate-600 dark:text-slate-400">Processing your PDF...</p>
                    </div>
                  )}

                  {results && (
                    <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                      <AlertDescription>
                        <div className="space-y-3">
                          <p className="font-semibold text-green-800 dark:text-green-200">✓ Processing Complete!</p>
                          
                          {/* Show compression stats if available */}
                          {results.compression_ratio !== undefined && (
                            <>
                              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-green-200 dark:border-green-700">
                                <div className="grid grid-cols-3 gap-4 text-center">
                                  <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Original</p>
                                    <p className="text-sm font-semibold">{(results.original_size / 1024).toFixed(1)} KB</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Compressed</p>
                                    <p className="text-sm font-semibold text-green-600">{(results.compressed_size / 1024).toFixed(1)} KB</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Saved</p>
                                    <p className="text-sm font-semibold text-blue-600">{results.compression_ratio.toFixed(1)}%</p>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Informative message for low compression */}
                              {results.compression_ratio < 10 && (
                                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                  <p className="text-xs text-blue-800 dark:text-blue-200">
                                    💡 <strong>Why minimal compression?</strong> Your PDF is already well-optimized or contains mostly text. PDFs with images typically compress more effectively.
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                          
                          {results.url && (
                            <Button
                              data-testid="download-result-btn"
                              onClick={() => triggerFileDownload(`${BACKEND_URL}${results.url}`, results.filename)}
                              className="w-full"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download {results.filename}
                            </Button>
                          )}
                          {results.pages && results.pages.map((page, idx) => (
                            <Button
                              key={`page-${page.page ?? idx}-${page.url ?? ''}`}
                              data-testid={`download-page-${idx + 1}-btn`}
                              onClick={() => triggerFileDownload(`${BACKEND_URL}${page.url}`, page.filename || `page-${page.page}.pdf`)}
                              variant="outline"
                              className="w-full"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download Page {page.page}
                            </Button>
                          ))}
                          {results.images && results.images.map((img, idx) => (
                            <Button
                              key={`img-${img.page ?? idx}-${img.url ?? ''}`}
                              data-testid={`download-image-${idx + 1}-btn`}
                              onClick={() => triggerFileDownload(`${BACKEND_URL}${img.url}`, img.filename || `image-${img.page}.jpg`)}
                              variant="outline"
                              className="w-full"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download Image {img.page}
                            </Button>
                          ))}
                          
                          {/* Compact Security Notice after downloads */}
                          <SecurityNotice compact />
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button
                    data-testid="process-pdf-btn"
                    onClick={handleProcessPDF}
                    disabled={processing || files.length === 0}
                    className="w-full"
                    size="lg"
                  >
                    {processing ? 'Processing...' : 'Process PDF'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      ) : (
        <section style={{ paddingTop: 'var(--section-padding-y)', paddingBottom: 'var(--section-padding-y)', paddingLeft: 'var(--container-padding-x)', paddingRight: 'var(--container-padding-x)' }}>
          <div className="container mx-auto">
            <h3 className="text-3xl font-bold text-center text-slate-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 'var(--space-2xl)' }}>
              Powerful PDF Tools
            </h3>
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" style={{ gap: 'var(--card-gap)', marginBottom: 'var(--space-3xl)' }}>
                {tools.map((tool) => (
                  <Card 
                    key={tool.id} 
                    className="relative cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                    onClick={() => {
                      setInstructionsTool(tool.id);
                      setShowInstructions(true);
                    }}
                  >
                    {/* Frosted Glass Overlay with Lock */}
                    <div className="absolute inset-0 bg-white/25 dark:bg-slate-900/25 backdrop-blur-[2px] flex items-center justify-center z-10 transition-all duration-300 hover:bg-white/35 dark:hover:bg-slate-900/35">
                      <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-blue-500/90 flex items-center justify-center shadow-lg">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>

                    {/* Card Content - Fully Visible */}
                    <CardHeader style={{ padding: 'var(--card-padding)' }}>
                      <div className="flex items-center justify-center w-14 h-14 mx-auto mb-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md">
                        {tool.icon}
                      </div>
                      <CardTitle className="text-center text-lg font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 'var(--space-xs)' }}>
                        {tool.name}
                      </CardTitle>
                      <CardDescription className="text-center text-sm text-slate-600 dark:text-slate-400">
                        {tool.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
              <Card className="border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-900">
                <CardContent style={{ paddingTop: 'var(--space-3xl)', paddingBottom: 'var(--space-3xl)', paddingLeft: 'var(--card-padding)', paddingRight: 'var(--card-padding)' }}>
                  <div className="w-16 h-16 mx-auto rounded-full bg-blue-500 flex items-center justify-center" style={{ marginBottom: 'var(--space-md)' }}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 'var(--space-md)' }}>
                    Sign Up to Access All Tools
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300 max-w-md mx-auto" style={{ marginBottom: 'var(--space-xl)' }}>
                    Create a free account to unlock all PDF tools. Start with 3 free files per day, no credit card required.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      data-testid="locked-signup-btn"
                      onClick={() => {
                        setAuthMode('register');
                        setShowAuth(true);
                      }} 
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Create Free Account
                    </Button>
                    <Button 
                      data-testid="locked-login-btn"
                      onClick={() => {
                        setAuthMode('login');
                        setShowAuth(true);
                      }} 
                      size="lg"
                      variant="outline"
                    >
                      Already have an account? Login
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{authMode === 'login' ? 'Login' : 'Sign Up'}</CardTitle>
              <CardDescription>Access professional PDF tools</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAuth} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email / Username</Label>
                  <Input
                    data-testid="auth-email-input"
                    id="email"
                    type="text"
                    placeholder="Enter your email or username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    data-testid="auth-password-input"
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button data-testid="auth-submit-btn" type="submit" className="w-full">
                  {authMode === 'login' ? 'Login' : 'Sign Up'}
                </Button>
                
                {authMode === 'login' && (
                  <Button
                    data-testid="forgot-password-btn"
                    type="button"
                    variant="link"
                    onClick={() => {
                      setShowAuth(false);
                      setShowForgotPassword(true);
                      setResetStep(1);
                    }}
                    className="w-full text-sm text-blue-600 hover:text-blue-700"
                  >
                    Forgot Password?
                  </Button>
                )}
                
                <Button
                  data-testid="auth-switch-btn"
                  type="button"
                  variant="ghost"
                  onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                  className="w-full"
                >
                  {authMode === 'login' ? 'Need an account? Sign up' : 'Have an account? Login'}
                </Button>
                <Button
                  data-testid="auth-close-btn"
                  type="button"
                  variant="outline"
                  onClick={() => setShowAuth(false)}
                  className="w-full"
                >
                  Close
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {resetStep === 1 ? 'Forgot Password' : 'Enter Reset Code'}
              </CardTitle>
              <CardDescription>
                {resetStep === 1 ? 'Enter your email to receive a reset code' : 'Check your email for the 6-digit code'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {resetStep === 1 ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label htmlFor="reset-email">Email Address</Label>
                    <Input
                      data-testid="reset-email-input"
                      id="reset-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button data-testid="send-code-btn" type="submit" className="w-full">
                    Send Reset Code
                  </Button>
                  <Button
                    data-testid="back-to-login-btn"
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setShowAuth(true);
                      setAuthMode('login');
                    }}
                    className="w-full"
                  >
                    Back to Login
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <Label htmlFor="reset-code">6-Digit Reset Code</Label>
                    <Input
                      data-testid="reset-code-input"
                      id="reset-code"
                      type="text"
                      placeholder="123456"
                      maxLength="6"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                      required
                      className="text-center text-2xl tracking-widest font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">Code expires in 15 minutes</p>
                  </div>
                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      data-testid="new-password-input"
                      id="new-password"
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </div>
                  <Button data-testid="reset-password-btn" type="submit" className="w-full">
                    Reset Password
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setResetStep(1)}
                    className="w-full"
                  >
                    Resend Code
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" style={{ paddingTop: 'var(--section-padding-y)', paddingBottom: 'var(--section-padding-y)', paddingLeft: 'var(--container-padding-x)', paddingRight: 'var(--container-padding-x)', marginTop: 'var(--space-3xl)' }}>
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 'var(--space-2xl)', marginBottom: 'var(--space-2xl)' }}>
            {/* Company Info */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">SmartPDF Tools</h3>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {contentSettings?.footer_promo_text || 'Professional PDF processing platform for all your document needs.'}
              </p>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy-policy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/cookie-policy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund-policy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    Refund Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* More Legal Links */}
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Policies</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/copyright-dmca" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    Copyright & DMCA
                  </Link>
                </li>
                <li>
                  <Link to="/disclaimer" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    Disclaimer
                  </Link>
                </li>
                <li>
                  <Link to="/acceptable-use-policy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    Acceptable Use Policy
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    Security & Privacy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/contact-support" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    Contact & Support
                  </Link>
                </li>
                <li className="text-slate-600 dark:text-slate-400">
                  support@smrtpdftools.com
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-200 dark:border-slate-700 text-center text-sm text-slate-600 dark:text-slate-400" style={{ paddingTop: 'var(--space-2xl)' }}>
            <p>{contentSettings?.footer_copyright || '© 2025 SmartPDF Tools. All rights reserved.'}</p>
            <p style={{ marginTop: 'var(--space-sm)' }}>{contentSettings?.footer_promo_text || 'Professional PDF processing platform'}</p>
          </div>
        </div>
      </footer>

      {/* Tool Instructions Panel */}
      <ToolInstructionsPanel
        tool={instructionsTool}
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        isAuthenticated={isAuthenticated}
        onOpenTool={() => {
          setShowInstructions(false);
          // Clear previous files and results when switching tools
          setFiles([]);
          setResults(null);
          setSelectedTool(instructionsTool);
          // Scroll to upload section after a brief delay
          scrollToUploadSection();
        }}
        onSignUp={() => {
          setShowInstructions(false);
          setAuthMode('register');
          setShowAuth(true);
        }}
      />
    </div>
  );
};

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const navigate = useNavigate();

  const checkPaymentStatus = useCallback(async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/subscription/check-status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Payment successful! You are now a Pro user!');
        } else {
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch (error) {
        setStatus('error');
      }
    };
    
    poll();
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams, checkPaymentStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Payment Status</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'checking' && (
            <>
              <Progress value={50} className="w-full" />
              <p>Verifying your payment...</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="text-6xl mb-4">✓</div>
              <h3 className="text-2xl font-bold text-green-600">Payment Successful!</h3>
              <p className="text-slate-600">Welcome to SmartPDF Tools Pro!</p>
              <Button data-testid="go-home-btn" onClick={() => navigate('/')} className="w-full">Go to Dashboard</Button>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="text-6xl mb-4">✗</div>
              <h3 className="text-2xl font-bold text-red-600">Payment Failed</h3>
              <p className="text-slate-600">Please try again or contact support.</p>
              <Button onClick={() => navigate('/')} className="w-full">Go Back</Button>
            </>
          )}
          {status === 'timeout' && (
            <>
              <div className="text-6xl mb-4">⏱</div>
              <h3 className="text-2xl font-bold text-amber-600">Processing...</h3>
              <p className="text-slate-600">Payment is being processed. Check your email for confirmation.</p>
              <Button onClick={() => navigate('/')} className="w-full">Go Back</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default App;